<template>
  <div>
    <div class="header">
      <h1 data-testid="page-title">
        <span class="small">
          Join Thousands of Students Using
          <br />
        </span>
        UniHomes!
      </h1>
      <h2>Electricity. Gas. Broadband. Water.<br />TV Licence. All in one.</h2>
    </div>
    <main class="container">
      <affiliate-blurb />

      <form
        class="form"
        novalidate
        autocomplete="on"
        @submit.prevent="submitForm"
      >
        <WarningPanel
          v-if="errorResponse"
          :title="'There\'s a problem...'"
          :msg="errorResponse"
          :severe="true"
        />

        <fieldset class="form__fieldset">
          <div class="form-item" :class="{ '--has-error': $v.leadType.$error }">
            <label class="form__label">
              <span class="form__label-text">Which Describes You?</span>
              <FormError
                v-if="$v.leadType.$error"
                data-testid="student-lead-error"
                msg="Please select an option"
              />
            </label>
            <div class="form__radiobuttons">
              <label
                for="studentLead"
                :class="{ active: leadType === 'Student' }"
                data-testid="student-lead"
              >
                <input
                  type="radio"
                  id="studentLead"
                  name="leadType"
                  v-model="leadType"
                  value="Student"
                  required
                />
                Student
              </label>
              <label
                for="notStudentLead"
                :class="{ active: leadType === 'Not a Student' }"
                data-testid="notStudentLead"
              >
                <input
                  type="radio"
                  id="notStudentLead"
                  name="leadType"
                  v-model="leadType"
                  value="Not a Student"
                  required
                />
                Not A Student
              </label>
            </div>
          </div>
          <div
            class="form-item"
            :class="{
              '--has-error': $v.firstName.$error || validationErrors.firstName,
            }"
          >
            <label class="form__label" for="first-name">
              <span class="form__label-text">First Name</span>
              <FormError
                v-if="$v.firstName.$error || validationErrors.firstName"
                data-testid="first-name-error"
                :msg="
                  validationErrors.firstName
                    ? validationErrors.firstName[0]
                    : 'First name required'
                "
              />
            </label>
            <input
              ref="firstNameField"
              class="form__input"
              data-testid="first-name"
              type="text"
              id="first-name"
              name="first-name"
              required
              autocomplete="given-name"
              v-model.trim="firstName"
              @blur="capitaliseName"
            />
          </div>

          <div
            class="form-item"
            :class="{
              '--has-error': $v.lastName.$error || validationErrors.lastName,
            }"
          >
            <label class="form__label" for="last-name">
              <span class="form__label-text">Last Name</span>
              <FormError
                v-if="$v.lastName.$error || validationErrors.lastName"
                data-testid="last-name-error"
                :msg="
                  validationErrors.lastName
                    ? validationErrors.lastName[0]
                    : 'Last name required'
                "
              />
            </label>
            <input
              class="form__input"
              data-testid="last-name"
              type="text"
              id="last-name"
              name="last-name"
              required
              autocomplete="family-name"
              v-model.trim="lastName"
              @blur="capitaliseName"
            />
          </div>

          <email-input
            name="email"
            label="Email Address"
            v-model.trim="emailAddress"
            :v="$v.emailAddress"
            :errorMessage="
              validationErrors.emailAddress
                ? validationErrors.emailAddress[0]
                : ''
            "
          />

          <div
            class="form-item"
            :class="{
              '--has-error':
                $v.telNumber.$error || validationErrors.mobileNumber,
            }"
          >
            <label class="form__label" for="mobile-number">
              <span class="form__label-text">Mobile Number</span>
              <FormError
                v-if="
                  ($v.telNumber.$error && !$v.telNumber.required) ||
                    validationErrors.mobileNumber
                "
                data-testid="phone-number-required-error"
                :msg="
                  validationErrors.mobileNumber
                    ? validationErrors.mobileNumber[0]
                    : 'Mobile number required'
                "
              />
            </label>
            <input
              class="form__input"
              type="tel"
              data-testid="mobile"
              id="mobile-number"
              name="mobile-number"
              required
              autocomplete="tel"
              v-model.trim="telNumber"
            />
          </div>

          <div
            v-if="shouldShowWhatsAppConsent"
            class="form-item whatsapp-consent"
            data-testid="whatsapp-consent"
          >
            <div class="whatsapp-consent__row">
              <input
                id="whatsapp_consent"
                v-model="whatsappConsent"
                name="whatsapp_consent"
                type="checkbox"
                class="form-checkbox__input"
                data-testid="whatsapp-consent-checkbox"
              />
              <label
                class="form-checkbox__label whatsapp-consent__label"
                for="whatsapp_consent"
              >
                <svg
                  class="whatsapp-consent__icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M0 23.9999L1.687 17.837C0.645998 16.033 0.0989998 13.988 0.0999998 11.891C0.103 5.33499 5.43799 0 11.993 0C15.174 0.000999998 18.16 1.24 20.406 3.48799C22.6509 5.73599 23.8869 8.72398 23.8859 11.902C23.8829 18.459 18.548 23.7939 11.993 23.7939C10.003 23.7929 8.04198 23.2939 6.30499 22.346L0 23.9999ZM6.59698 20.193C8.27298 21.188 9.87298 21.784 11.989 21.785C17.437 21.785 21.875 17.351 21.878 11.9C21.88 6.43799 17.463 2.01 11.997 2.008C6.54498 2.008 2.11 6.44199 2.108 11.892C2.107 14.117 2.75899 15.783 3.85399 17.526L2.85499 21.174L6.59698 20.193ZM17.984 14.729C17.91 14.605 17.712 14.531 17.414 14.382C17.117 14.233 15.656 13.514 15.383 13.415C15.111 13.316 14.913 13.266 14.714 13.564C14.516 13.861 13.946 14.531 13.773 14.729C13.6 14.927 13.426 14.952 13.129 14.803C12.832 14.654 11.874 14.341 10.739 13.328C9.85598 12.54 9.25798 11.567 9.08598 11.269C8.91298 10.972 9.06798 10.811 9.21598 10.663C9.34998 10.53 9.51298 10.316 9.66198 10.142C9.81298 9.96998 9.86198 9.84598 9.96198 9.64698C10.061 9.44898 10.012 9.27498 9.93698 9.12598C9.86198 8.97798 9.26798 7.51498 9.02098 6.91998C8.77898 6.34099 8.53398 6.41899 8.35198 6.40999L7.78198 6.39999C7.58398 6.39999 7.26198 6.47399 6.98998 6.77199C6.71798 7.06998 5.94999 7.78798 5.94999 9.25098C5.94999 10.714 7.01498 12.127 7.16298 12.325C7.31198 12.523 9.25798 15.525 12.239 16.812C12.948 17.118 13.502 17.301 13.933 17.438C14.645 17.664 15.293 17.632 15.805 17.556C16.376 17.471 17.563 16.837 17.811 16.143C18.059 15.448 18.059 14.853 17.984 14.729Z"
                    fill="#25D366"
                  />
                </svg>
                Let us chat with you via WhatsApp
              </label>
              <InfoBubble
                text="Receive quote updates and account confirmations directly to your WhatsApp."
                title="WhatsApp consent information"
                icon-color="#3B4143"
              />
            </div>
          </div>

          <div
            class="form-item pre-signup-privacy__confirm"
            :class="{ '--has-error': $v.agreedToPolicy.$error }"
          >
            <div class="form-checkbox">
              <FormError
                v-if="$v.agreedToPolicy.$error && !$v.agreedToPolicy.sameAs"
                data-testid="privacy-policy-required-error"
                msg="You must agree to the privacy policy"
              />
              <input
                class="form-checkbox__input"
                type="checkbox"
                name="privacyPolicy"
                id="privacyPolicy"
                data-testid="privacy-policy-input"
                v-model="agreedToPolicy"
              />
              <label
                for="privacyPolicy"
                class="form-checkbox__label privacy-policy-check"
                data-testid="privacy-policy-label"
              >
                By ticking this box you agree to be contacted regarding your
                utilities quote, and agree to our
                <a
                  href="https://www.unihomes.co.uk/privacy-and-cookie-policies"
                  target="_blank"
                  >privacy policy</a
                >.
              </label>
            </div>
          </div>
        </fieldset>

        <button
          class="btn btn--big form__submit w-100"
          data-testid="submit-pre-signup"
          type="submit"
          @click="trackSubmitBtn"
        >
          Next
        </button>
      </form>

      <div class="marketing-points">
        <div class="icon">
          <HouseIcon />
        </div>
        <div class="title">
          All Inclusive
        </div>
        <div class="subtitle">
          We cover all of your utilities in one easy payment.
        </div>
        <div class="icon">
          <StarIcon />
        </div>
        <div class="title">
          Rated Excellent
        </div>
        <div class="subtitle">
          Thousands of students have rated us Excellent on Trustpilot.
        </div>
        <div class="icon">
          <LightbulbIcon />
        </div>
        <div class="title">
          Uncapped Energy
        </div>
        <div class="subtitle">
          Never worry about over usage or an unexpected bill.
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { mapActions, mapMutations } from "vuex";
import { required, sameAs } from "vuelidate/lib/validators";
import FormError from "@/components/forms/FormError";
import InfoBubble from "@/components/InfoBubble.vue";
import WarningPanel from "@/components/WarningPanel";
import LightbulbIcon from "@/assets/svg/icon-lightbulb.svg";
import HouseIcon from "@/assets/svg/icon-house.svg";
import StarIcon from "@/assets/svg/star.svg";
import { findDiscountCode } from "@/utility/discount";
import AffiliateBlurb from "@/components/AffiliateBlurb";
import EmailInput from "@/components/EmailInput";

export default {
  name: "PreSignup",
  components: {
    AffiliateBlurb,
    EmailInput,
    FormError,
    InfoBubble,
    LightbulbIcon,
    HouseIcon,
    StarIcon,
    WarningPanel,
  },
  data() {
    return {
      errorResponse: "",
      agreedToPolicy: false,
    };
  },
  computed: {
    leadType: {
      get() {
        return this.$store.getters.leadType;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { leadType: value });
      },
    },
    firstName: {
      get() {
        return this.$store.getters.firstName;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { firstname: value });
      },
    },
    lastName: {
      get() {
        return this.$store.getters.lastName;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { lastname: value });
      },
    },
    emailAddress: {
      get() {
        return this.$store.getters.emailAddress;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { email: value });
      },
    },
    telNumber: {
      get() {
        return this.$store.getters.telNumber;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { tel: value });
      },
    },
    validationErrors() {
      return this.$store.state.validationErrors;
    },
    shouldShowWhatsAppConsent() {
      return this.$store.getters.shouldShowWhatsAppConsent;
    },
    whatsappConsent: {
      get() {
        return this.$store.state.personalDetails.whatsappConsent;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", {
          whatsappConsent: value,
        });
      },
    },
  },
  methods: {
    ...mapActions([
      "getAffiliateMessage",
      "createPreSignupLead",
      "setValidationErrors",
      "clearValidationErrors",
    ]),
    ...mapMutations({
      hideBackButton: "HIDE_BACK_BTN",
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      capitaliseName: "CAPITALISE_NAME",
    }),
    submitForm() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.showLoadingPanel();
        this.errorResponse = "";

        this.capitaliseName();

        this.clearValidationErrors();

        const discountCode = findDiscountCode(this.$route.query, this.$store);

        this.createPreSignupLead({
          agreedToPolicy: this.agreedToPolicy,
          discountCode,
        })
          .then(() => {
            this.$router.push("/household");
          })
          .catch((error) => {
            if (!error.response) {
              this.errorResponse = "Network error accessing the UniHomes API";
            } else if (error.response.status === 422) {
              this.setValidationErrors(error.response.data.errors);
            }
          })
          .finally(() => {
            this.hideLoadingPanel();
          });
      }
    },
    trackSubmitBtn() {
      this.trackGoogleEvent("Lead", "Presignup", "Account Capture");
    },
  },
  created() {
    this.hideBackButton();

    this.trackGoogleEvent("Land on Presignup", "Presignup", "Presignup Page");

    // check for affiliate code in url and validate against endpoint
    let affiliateCode = this.$route.query.aff || localStorage.affiliateCode;
    if (affiliateCode) {
      this.getAffiliateMessage(affiliateCode);
    }
  },
  validations() {
    return {
      leadType: {
        required,
      },
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      emailAddress: {
        required,
      },
      telNumber: {
        required,
      },
      agreedToPolicy: {
        sameAs: sameAs(() => true),
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.header {
  * {
    color: $brand-dark-blue;
  }

  h1 {
    line-height: 0.9;
    font-size: 50px;
    font-weight: $font-bold;
    margin-bottom: 0;

    .small {
      font-size: 30px;
      font-weight: $font-regular;

      @include media("<medium") {
        font-size: 22px;
      }
    }

    @include media("<medium") {
      margin-top: 1rem;
    }
  }

  h2 {
    font-size: 16px;
    font-weight: $font-regular;

    @include media(">=medium") {
      margin-bottom: $spacing-large;
    }

    @include media("<medium") {
      margin-left: 3rem;
      margin-right: 3rem;
    }
  }
}

.whatsapp-consent {
  margin-bottom: 16px;
  margin-left: -24px;
  margin-right: -24px;
  padding: 16px 24px 20px;
  border-radius: 8px;
  background: linear-gradient(
    180deg,
    rgba(37, 211, 102, 0.1) 93.75%,
    rgba(19, 109, 53, 0) 100%
  );
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;

  @include media(">=medium") {
    font-size: 16px;
  }

  &__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__label {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding-left: calc(4.5rem);
    font-size: 14px;
    margin-right: 8px;
    cursor: pointer;
  }

  &__icon {
    flex-shrink: 0;
    margin-right: 8px;
  }
}

.privacy-policy-check {
  a {
    font-weight: $font-bold;
    color: $brand-dark-blue;
  }
}

.marketing-points {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto auto;
  grid-auto-flow: column;
  grid-column-gap: 2rem;

  justify-items: center;

  margin: 4rem 0;
  text-align: center;

  @include media("<medium") {
    margin: 2rem 0.5rem 4rem 0.5rem;
    grid-column-gap: 0.5rem;
  }

  * {
    margin: 0.5rem 0;
  }

  .icon {
    color: $brand-dark-blue;

    svg {
      height: 32px;
    }
  }

  .title {
    font-size: 18px;
    font-weight: 600;
    color: $brand-dark-blue;

    @include media("<medium") {
      font-size: 13px;
    }
  }

  .subtitle {
    font-size: 14px;
    font-weight: $font-regular;

    @include media("<medium") {
      font-size: 12px;
    }
  }
}
</style>
